import React, { useState, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  EyeIcon, 
  EyeOffIcon, 
  MenuAlt4Icon,
  BookOpenIcon,
  ClipboardListIcon,
  DotsVerticalIcon,
  PencilIcon,
  TrashIcon,
  FilmIcon,
  DocumentTextIcon,
  DocumentIcon
} from '@heroicons/react/outline';
import DeleteConfirmModal from '../../../../../../components/common/DeleteConfirmModal';
import LessonPreviewModal from './modals/LessonPreviewModal';
import PermissionGuard from '../../../../../common/PermissionGuard';

const Lesson = ({ lesson, onStatusChange, onDelete, onEdit }) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const buttonRef = useRef(null);
  
  // إغلاق القائمة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActions && !event.target.closest('.dropdown-menu')) {
        setShowActions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActions]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: lesson.id,
    transition: {
      duration: 150, // تسريع مدة الانتقال
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)' // منحنى حركة أكثر سلاسة
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // تحديد أيقونة نوع المحتوى
  const getTypeIcon = () => {
    // استخدام lesson_type إذا كان موجوداً وإلا استخدام type
    const effectiveType = lesson.lesson_type || lesson.type;
    // نوع الدرس الفعلي لعرض الأيقونة المناسبة
    
    switch (effectiveType) {
      case 'video':
      case 'video_url':
        return <FilmIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" />;
      case 'video_embed':
        return <FilmIcon className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />;
      case 'video_file':
        return <FilmIcon className="w-5 h-5 text-purple-500 dark:text-purple-400" />;
      case 'text':
        return <DocumentTextIcon className="w-5 h-5 text-green-500 dark:text-green-400" />;
      case 'file':
        return <DocumentIcon className="w-5 h-5 text-orange-500 dark:text-orange-400" />;
      default:
        return <BookOpenIcon className="w-5 h-5 text-green-500 dark:text-green-400" />;
    }
  };

  // تحديد نص نوع المحتوى
  const getTypeText = () => {
    // استخدام lesson_type إذا كان موجوداً وإلا استخدام type
    const effectiveType = lesson.lesson_type || lesson.type;
    
    switch (effectiveType) {
      case 'video':
      case 'video_url':
        return 'درس فيديو';
      case 'video_embed':
        return 'رابط امبد';
      case 'video_file':
        return 'درس فيديو';
      case 'text':
        return 'نص';
      case 'file':
        return 'ملف';
      default:
        return 'درس';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg
        border border-gray-200 dark:border-gray-700 shadow-sm cursor-default overflow-visible
        ${isDragging ? 'opacity-50 shadow-lg scale-105' : ''}`}
    >
      <div className="flex items-center gap-3 overflow-visible">
        {/* مقبض السحب */}
        <div className="relative group overflow-visible flex items-center">
          <div
            {...attributes}
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <MenuAlt4Icon className="w-4 h-4" />
          </div>
          <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
            اسحب لإعادة الترتيب
          </div>
        </div>

        {/* أيقونة نوع المحتوى */}
        <div className="relative group overflow-visible flex items-center">
          {getTypeIcon()}
          <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
            {getTypeText()}
          </div>
        </div>

        <div className="relative overflow-visible">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              {lesson.title}
            </h4>
            <div className="flex items-center gap-1">
              {/* أيقونة الدولار لتوضيح حالة الدرس (مجاني/مدفوع) */}
              <span className="relative group overflow-visible flex items-center">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className={`w-4 h-4 ${lesson.is_free ? 'text-gray-400' : 'text-green-500'}`}
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z" />
                </svg>
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-300 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[65] transition-all duration-75">
                  {lesson.is_free ? 'درس مجاني' : 'درس مدفوع'}
                </div>
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {lesson.duration ? `المدة: ${lesson.duration} دقيقة` : ''}
              </p>
            </div>
          </div>
          <div 
            onClick={() => onEdit(lesson)}
            className="cursor-pointer absolute invisible opacity-0 hover:visible hover:opacity-100 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75"
          >
            عرض تفاصيل الدرس
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-visible h-full">
        {/* زر المعاينة */}
        <div className="relative group overflow-visible flex items-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowPreviewModal(true);
            }}
            className="text-blue-500 hover:text-blue-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
          </button>
          <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
            معاينة الدرس
          </div>
        </div>

        {/* زر التعديل */}
        <PermissionGuard permission="edit_course_content">
          <div className="relative group overflow-visible flex items-center flex items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit && onEdit();
              }}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              تعديل الدرس
            </div>
          </div>
        </PermissionGuard>

        {/* مؤشر حالة النشر */}
        <div className="relative group overflow-visible flex items-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStatusChange && onStatusChange(lesson.id);
            }}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
          >
            {lesson.is_published ? (
              <EyeIcon className="w-4 h-4 text-green-500" />
            ) : (
              <EyeOffIcon className="w-4 h-4" />
            )}
          </button>
          <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
            {lesson.is_published ? 'إخفاء الدرس' : 'نشر الدرس'}
          </div>
        </div>

        {/* زر الحذف */}
        <PermissionGuard permission="delete_course_content">
          <div className="relative group overflow-visible flex items-center flex items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteModal(true);
              }}
              className="text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              حذف الدرس
            </div>
          </div>
        </PermissionGuard>
      </div>
      
      {/* مودال تأكيد الحذف */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          // تأكيد حذف الدرس
          onDelete && onDelete(lesson);
          setShowDeleteModal(false);
        }}
        itemType="الدرس"
        itemTitle={lesson.title}
      />
      {/* مودال معاينة الدرس */}
      <LessonPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        lesson={lesson}
      />
    </div>
  );
};

export default Lesson;
